import * as Sentry from "@sentry/react";

import { Paragraph, StyledAlert } from "App/global-style";
import {
  getCollectionOfDittoComponents,
  useTexts,
} from "Components/TextContext";
import { Form, FormikProps } from "formik";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { UpdatePriceRequestProps } from "Components/Features/CheckPrice/Products/productInterfaces";
import { ProductDetailsTile } from "Components/Features/CheckPrice/Tiles/ProductDetailsTile";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { AlertDescription } from "Components/TextContext/commonComponentEnums";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { BackendProductInfo } from "Features/CheckPrice/Products/productConstants";
import { useCurrentCart } from "Store/cart";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { reverseAndPadDDMMYYYY } from "Utils/dateUtils";
import { mapValidationErrorsDitto } from "Utils/formUtils";
import { AKA_CHILD_ABOUT_STEP } from "..";
import { getChildProductDetailsInitialFormValues } from "../../Child/ProductDetails/childProductDetailsUtils";
import { isNotChildError } from "../akaChildUtils";
import { AkaChildProductDetailsFormElements } from "./AkaChildProductDetailsFormElements";
import { AkaChildProductDetailsFormValues } from "./akaChildProductDetailsInterfaces";
import { validationSchema } from "./akaChildProductDetailsValidation";

interface Props {
  updatePriceRequest: (props: UpdatePriceRequestProps) => void;
}

export const AkaChildProductDetails = ({
  updatePriceRequest,
}: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const { getText } = useTexts();
  const cart = useCurrentCart();
  const initialFormState = useFormState<AkaChildProductDetailsFormValues>(
    AKA_CHILD_ABOUT_STEP.id,
    getChildProductDetailsInitialFormValues(cart),
  );
  const [currentDate, setCurrentDate] = useState("");

  const { shorttitle: shortTitleComponents } = getCollectionOfDittoComponents(
    TextComponentNameLevel1.AKA_CHILD,
  );
  const {
    priceCalculation: { priceCalculationResult },
  } = useSelector((state: ReduxState) => state);

  const handleSubmit = (formValues: AkaChildProductDetailsFormValues): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: AKA_CHILD_ABOUT_STEP.id,
        formState: {
          ...formValues,
          childBirthDate: formValues.childBirthDate,
        },
      }),
    );
    updatePriceRequest({
      productPriceUrl: BackendProductInfo.AKA_CHILD.priceReqUrl,
      stepForwardOnSuccess: true,
      updatedProductPriceRequestInfo: {
        childDateOfBirth: formValues.childBirthDate
          ? reverseAndPadDDMMYYYY(formValues.childBirthDate)
          : undefined,
      },
    });
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductDetails - ${INSURANCE_NAME_VALUE.AKA_CHILD}`,
        );
      }}
    >
      <ProductDetailsTile
        header={AKA_CHILD_ABOUT_STEP.title}
        validationSchema={validationSchema()}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
      >
        {(formProps: FormikProps<AkaChildProductDetailsFormValues>) => {
          const childError = isNotChildError(currentDate);
          return (
            <Form>
              <Paragraph>
                {getText("akachildform.ingress.productintro")}
              </Paragraph>
              <AkaChildProductDetailsFormElements
                formProps={formProps}
                childError={childError}
                setCurrentDate={setCurrentDate}
              />
              <StyledAlert title={getText(AlertDescription.EDH_ALERT)} />
              {priceCalculationResult.error && (
                <SystemErrorAlert alertId="priceCalculation" />
              )}
              <ScrollToFirstError />
              <ErrorsSummedUp
                errors={mapValidationErrorsDitto(
                  formProps.errors,
                  shortTitleComponents,
                )}
                isVisible
              />
              <NavigationButtons isLoading={priceCalculationResult.isLoading} />
              <InputFailureTracker
                productName={INSURANCE_NAME_VALUE.AKA_CHILD}
                stepId={AKA_CHILD_ABOUT_STEP.id}
              />
            </Form>
          );
        }}
      </ProductDetailsTile>
    </Sentry.ErrorBoundary>
  );
};
